import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuthStore } from '../../store/useAuthStore'
import { useCoreTodayStore } from '../../store/useCoreTodayStore'
import type { NormalizedImage } from '../../services/imageProcessingService'
import { withAlpha } from '../../utils/themeUtils'
import { VoiceDictationButton } from '../shared/VoiceDictationButton'
import { Button } from '../ui/button'
import { Modal } from '../ui/modal'
import { Textarea } from '../ui/textarea'
import { useToast } from '../ui/use-toast'
import { CoreErrorDiagnostic } from './CoreErrorDiagnostic'
import { CaptureImagePicker } from './CaptureImagePicker'
import { CaptureLinkInput } from './CaptureLinkInput'
import { normalizeCaptureLink } from '../../services/coreCaptureService'

export function CaptureComposer() {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { toast } = useToast()
  const userId = useAuthStore((state) => state.user?.id)
  const isOpen = useCoreTodayStore((state) => state.isCaptureOpen)
  const isCapturing = useCoreTodayStore((state) => state.isCapturing)
  const error = useCoreTodayStore((state) => state.error)
  const closeCapture = useCoreTodayStore((state) => state.closeCapture)
  const clearError = useCoreTodayStore((state) => state.clearError)
  const captureText = useCoreTodayStore((state) => state.captureText)
  const captureImage = useCoreTodayStore((state) => state.captureImage)
  const captureLink = useCoreTodayStore((state) => state.captureLink)
  const [draft, setDraft] = useState('')
  const [image, setImage] = useState<NormalizedImage | null>(null)
  const [isLinkActive, setIsLinkActive] = useState(false)
  const [link, setLink] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) clearError()
  }, [clearError, isOpen])

  const saveCapture = async (entryMethod: 'keyboard' | 'dictation' = 'keyboard', text = draft) => {
    if (!userId || (!text.trim() && !image && !isLinkActive) || isCapturing) return

    let normalizedLink: string | null = null
    if (isLinkActive) {
      try {
        normalizedLink = normalizeCaptureLink(link)
        setLinkError(null)
      } catch {
        setLinkError(t('core.capture.link.error'))
        return
      }
    }

    try {
      const outcome = normalizedLink
        ? await captureLink(userId, normalizedLink, text, entryMethod)
        : image
          ? await captureImage(userId, text, image, entryMethod)
          : await captureText(userId, text, entryMethod)
      setDraft('')
      setImage(null)
      setIsLinkActive(false)
      setLink('')
      setLinkError(null)
      const profiles = outcome.contextRecords.filter((record) => record.form === 'profile')
      const resultKey = outcome.organizationState === 'failed'
        ? 'core.capture.results.savedOnly'
        : outcome.reviewItem
          ? 'core.capture.results.review'
          : outcome.action
            ? 'core.capture.results.action'
            : profiles.length
              ? 'core.capture.results.connected'
              : 'core.capture.results.context'
      toast({
        title: t('core.capture.saved'),
        description: t(resultKey, { subjects: profiles.map((profile) => profile.title).join(', ') }),
      })
    } catch {
      // The store preserves the draft and exposes the actionable error inline.
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={closeCapture} title={t('core.capture.title')} className="sm:max-w-xl">
      <div className="space-y-5">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: colors.primary }}>
            <SparklesIcon className="h-4 w-4" />
            {t('core.capture.eyebrow')}
          </div>
          <p className="max-w-lg text-sm leading-6" style={{ color: colors.textSecondary }}>
            {t('core.capture.description')}
          </p>
        </div>

        <div
          className="rounded-[24px] border p-3"
          style={{
            borderColor: withAlpha(colors.border, 'CC'),
            backgroundColor: colors.surfaceAlt || colors.surface,
          }}
        >
          <Textarea
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('core.capture.placeholder')}
            aria-label={t('core.capture.inputLabel')}
            className="min-h-36 resize-none border-0 bg-transparent px-1 py-1 text-base leading-7 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault()
                void saveCapture()
              }
            }}
          />
          <div className="mt-3 px-1">
            {isLinkActive ? (
              <CaptureLinkInput
                active
                value={link}
                error={linkError}
                disabled={!userId || isCapturing}
                onAdd={() => undefined}
                onChange={(value) => {
                  setLink(value)
                  if (linkError) setLinkError(null)
                }}
                onRemove={() => {
                  setIsLinkActive(false)
                  setLink('')
                  setLinkError(null)
                }}
              />
            ) : (
              <div className={image ? undefined : 'flex flex-wrap items-center gap-2'}>
                <CaptureImagePicker
                  value={image}
                  disabled={!userId || isCapturing}
                  onChange={setImage}
                />
                {!image ? (
                  <CaptureLinkInput
                    active={false}
                    value=""
                    disabled={!userId || isCapturing}
                    onAdd={() => {
                      setImage(null)
                      setIsLinkActive(true)
                    }}
                    onChange={() => undefined}
                    onRemove={() => undefined}
                  />
                ) : null}
              </div>
            )}
          </div>


          <div className="mt-3 flex items-center justify-between gap-3 border-t pt-3" style={{ borderColor: withAlpha(colors.border, 'A0') }}>
            <VoiceDictationButton
              disabled={!userId || isCapturing}
              onTranscript={(transcript) => void saveCapture('dictation', transcript)}
            />
            <Button
              type="button"
              onClick={() => void saveCapture()}
              disabled={!userId || (!draft.trim() && !image && !isLinkActive) || isCapturing}
              className="min-w-28 rounded-2xl"
            >
              <span>{isCapturing ? t('core.capture.organizing') : t('core.capture.submit')}</span>
              <ArrowUpIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {error ? (
          <div
            role="alert"
            className="rounded-2xl border px-4 py-3 text-sm leading-5"
            style={{
              borderColor: withAlpha(colors.error, '55'),
              backgroundColor: withAlpha(colors.error, '0D'),
              color: colors.text,
            }}
          >
            <p className="font-semibold">{t('core.capture.errorTitle')}</p>
            <p className="mt-1" style={{ color: colors.textSecondary }}>{t('core.capture.errorDescription')}</p>
            <CoreErrorDiagnostic error={error} color={colors.textSecondary} />
          </div>
        ) : null}

        <p className="text-xs leading-5" style={{ color: colors.textSecondary }}>
          {t('core.capture.hint')}
        </p>
      </div>
    </Modal>
  )
}
